import {Injectable} from "@angular/core";

import {ReportError} from "app/app.service";
import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {AutoRetryRequest} from "app/services/domain/base.service";
import {TagBaselineDefinitionVersionsService} from "app/services/domain/tag-baseline-versions.service";
import * as Models from "app/services/proxy/model/models";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";

@Injectable()
export class TagBaselineDefinitionsService extends SharedSvc.BaseDefinitionService<Models.TagBaselineConfiguration, Models.TagBaselineDefinition, Models.TagBaselineDefinitionVersion, TagBaselineDefinitionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.TagBaselineDefinition, TagBaselineDefinitionExtended.newInstance);
    }

    createDefinition(): Promise<Models.TagBaselineDefinition>
    {
        return this.api.tagBaselineDefinitionsApi.create();
    }

    setState(sysId: string,
             model: Models.TagBaselineDefinition): Promise<Models.TagBaselineDefinition>
    {
        return this.api.tagBaselineDefinitionsApi.setState(sysId, model);
    }

    remove(sysId: string): Promise<boolean>
    {
        return this.api.tagBaselineDefinitionsApi.remove(sysId);
    }

    @ReportError
    public async getAll(): Promise<Models.RecordIdentity[]>
    {
        return this.api.tagBaselineDefinitionsApi.getAll();
    }

    public getHistory(sysId: string): Promise<Models.TagBaselineDefinitionVersion[]>
    {
        return this.api.tagBaselineDefinitionsApi.getHistory(sysId);
    }

    //--//

    protected cachePrefix(): string { return "TagBaselineDefinition"; }

    protected getRaw(id: string): Promise<Models.TagBaselineDefinition>
    {
        return AutoRetryRequest(20, () => this.api.tagBaselineDefinitionsApi.get(id));
    }

    protected getBatchRaw(ids: string[]): Promise<Models.TagBaselineDefinition[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => TagBaselineDefinitionExtended.newIdentity(id))});
        return this.api.tagBaselineDefinitionsApi.batch(request);
    }

    protected canBatch(): boolean
    {
        return true;
    }
}

export class TagBaselineDefinitionExtended extends SharedSvc.ExtendedDefinitionModel<Models.TagBaselineConfiguration, Models.TagBaselineDefinition, Models.TagBaselineDefinitionVersion>
{
    static newInstance(svc: TagBaselineDefinitionsService,
                       model: Models.TagBaselineDefinition): TagBaselineDefinitionExtended
    {
        return new TagBaselineDefinitionExtended(svc, model, "TagBaselineDefinition");
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "TagBaselineDefinition",
                                                     sysId: sysId
                                                 });
    }

    //--//

    protected definitionSvc(): TagBaselineDefinitionsService
    {
        return this.domain.tagBaselineDefinitions;
    }

    protected definitionVersionSvc(): TagBaselineDefinitionVersionsService
    {
        return this.domain.tagBaselineDefinitionVersions;
    }

    //--//

    get isOwned(): boolean
    {
        const userDetails = this.domain.users.userDetails;
        return userDetails.is_account_admin && this.model.account_id === userDetails.user.master_account_id;
    }

    async setState(model: Models.TagBaselineDefinition): Promise<void>
    {
        if (this.model.sysId)
        {
            let svc = this.domain.tagBaselineDefinitions;
            svc.errors.dismiss();

            await svc.flush(this);
            this.model.title       = model.title;
            this.model.description = model.description;
            this.model.scope       = model.scope;
            this.model.is_active   = model.is_active || false;

            this.model = await svc.api.tagBaselineDefinitionsApi.setState(this.model.sysId, this.model);
        }
    }
}

